import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { usePlatformOutletContext } from '../Platform/usePlatFormOutletContext'
import {
  Tree,
  TreeItem,
  TreeItemLayout,
  TreeItemValue,
  TreeOpenChangeData,
  TreeOpenChangeEvent,
} from '@fluentui/react-components'
import { ChevronRight16Regular, ChevronDown16Regular } from '@fluentui/react-icons'
import clsx from 'clsx'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { IconDocumentOutline } from '@/components/icons'

type MessagesLandingMenuItem = RestSchemas['Messages.Landing.MenuItem']

export function Menu() {
  const { appId, projectId } = usePlatformOutletContext()
  const { getLandingApiGetPlatformOperations } = createRestClient()

  const {
    data: menu,
    isPending,
    isError,
  } = getLandingApiGetPlatformOperations.useQuery({ query: { appId, projectId, grouping: 'menu' } })

  if (isError || isPending) return null

  return (
    <nav className="pr-5">
      <MenuTree menu={menu?.items} level={0} />
    </nav>
  )
}

const MenuTree = ({ menu, level }: { menu: MessagesLandingMenuItem[]; level: number }) => {
  const [openItems, setOpenItems] = useState<TreeItemValue[]>(() => [])
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const handleOpenChange = (_event: TreeOpenChangeEvent, data: TreeOpenChangeData) => {
    setOpenItems((curr) => (data.open ? [...curr, data.value] : curr.filter((value) => value !== data.value)))
  }

  const isRoot = level === 0

  useEffect(() => {
    const openParent = menu.find((item) => item.items?.some((item) => item.page_id === pathname))
    if (openParent) {
      setOpenItems((curr) => [...curr, openParent.page_id])
    }
  }, [menu, pathname])

  return (
    <Tree
      aria-label={isRoot ? 'Expand Icon' : undefined}
      openItems={isRoot ? openItems : undefined}
      onOpenChange={isRoot ? handleOpenChange : undefined}
      appearance="subtle-alpha"
    >
      {menu.map((menuItem) => {
        const isLeaf = menuItem.items?.length === 0
        const isActive = pathname === menuItem.page_id
        return (
          <TreeItem
            key={menuItem.page_id}
            itemType={isLeaf ? 'leaf' : 'branch'}
            value={menuItem.page_id}
            onClick={isLeaf ? () => navigate(`${menuItem.page_id}?${searchParams.toString()}`) : undefined}
          >
            <TreeItemLayout
              className={clsx('relative !px-3', {
                'justify-between': !isLeaf,
                'mt-4': !isLeaf || (isLeaf && isRoot),
                '!pl-9': level > 0,
              })}
              expandIcon={null}
              iconAfter={
                isLeaf ? undefined : openItems.includes(menuItem.page_id) ? (
                  <ChevronDown16Regular />
                ) : (
                  <ChevronRight16Regular />
                )
              }
            >
              <div
                className={clsx({
                  'absolute left-1 top-[calc(50%-12px)] h-[24px] w-[4.5px] rounded-full bg-primary': isActive,
                  // 'bg-primary absolute left-1 top-0 h-full w-[4px] rounded-full': isActive,
                })}
              />
              <div className={clsx('flex items-center gap-x-1')}>
                {level === 0 && (
                  <DynamicIcon
                    size={20}
                    iconName={menuItem.icon_name ?? ''}
                    lazyLoad={false}
                    fallback={<IconDocumentOutline size={24} />}
                  />
                )}
                <span
                  className={clsx('whitespace-nowrap font-light', {
                    'font-normal text-grey-darkest': !isLeaf && isRoot,
                    '!font-bold text-grey-darkest': isLeaf && isActive,
                  })}
                >
                  {menuItem.name}
                </span>
              </div>
            </TreeItemLayout>
            {!isLeaf && <MenuTree menu={menuItem.items} level={level + 1} />}
          </TreeItem>
        )
      })}
    </Tree>
  )
}
